import { motion } from "framer-motion";
import { ChevronLeft, FileText, CheckCircle2, AlertTriangle, ShieldCheck, Scale, CreditCard, Ban, Gavel, HelpCircle, Mail, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SeoHelmet from "@/components/SeoHelmet";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#030303] text-white selection:bg-white/10 selection:text-white">
      <SeoHelmet
        title="Terms of Service"
        description="Review the Alvaru terms of service. Understand the rules for using our CV tailoring Chrome extension and web application."
        canonical="https://www.alvaru.com/terms"
      />
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-500/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 pt-12 pb-24">
        {/* Navigation */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-12"
        >
          <Link to="/">
            <Button variant="ghost" className="text-white/60 hover:text-white hover:bg-white/5 group pl-0">
              <ChevronLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
              Back to landing page
            </Button>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-medium mb-6">
            <Scale className="w-3 h-3" />
            <span>Legal Agreement</span>
          </div>
          <h1 className="text-5xl font-bold mb-6 tracking-tight text-white uppercase italic">Terms of Service</h1>
          <p className="text-white/40 text-lg">
            Last updated: <span className="text-white/60">April 4, 2026</span>
          </p>
        </motion.div>

        {/* Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-12"
        >
          {/* Section 1 */}
          <section className="group">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">1. Acceptance of terms</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  By using Alvaru, you agree to these terms. If you do not agree, do not use the service.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="group">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Sparkles className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">2. Description of service</h2>
                <p className="text-white/50 leading-relaxed text-lg">
                  Alvaru is an AI-powered tool that tailors your existing CV/resume to match specific job descriptions. The service generates reformatted and reworded versions of your CV, and for Pro users, an accompanying cover letter, as downloadable PDFs.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <FileText className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">3. Your content</h2>
                <p className="text-white/50 leading-relaxed text-lg italic bg-white/[0.02] p-4 rounded-xl border border-white/5 mb-4">
                  You retain ownership of all content you upload (CVs, resumes).
                </p>
                <p className="text-white/50 leading-relaxed text-lg">
                  By uploading content, you grant Alvaru a worldwide, royalty-free, non-exclusive license to use, process, store, analyze, and derive insights from your content for the purposes of: providing and improving the service, developing new features, generating aggregated and anonymized analytics, training internal systems, and any other legitimate business purpose. We do not claim ownership of your content, but this license survives account deletion for any anonymized or aggregated data already derived from your content.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-red-500/10 border border-red-500/20 group-hover:border-red-500/40 transition-colors">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3 text-red-100 italic">4. Accuracy and honesty</h2>
                <div className="space-y-4 text-white/50 text-lg">
                  <p>
                    Alvaru is designed to reframe and reword your existing experience — not to fabricate it. However, AI-generated output (including CVs and cover letters) may contain errors, unintended changes, hallucinated content, formatting issues, or inaccuracies. 
                  </p>
                  <p className="border-l-2 border-red-500/30 pl-4 py-1 text-white/70">
                    The AI may occasionally add, alter, or misrepresent information from your original CV. You are solely responsible for reviewing all generated content before submitting it to employers or any third party.
                  </p>
                  <p>
                    Alvaru is not liable for any inaccuracies, fabrications, or quality issues in generated content. By using the service, you acknowledge that AI output requires human review and that you accept full responsibility for any content you choose to use. Alvaru is a document formatting and generation tool only — it does not guarantee interviews, job offers, callbacks, or any specific employment outcome.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-white/20 transition-colors">
                <Ban className="w-5 h-5 text-white/60" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-3">5. Acceptable use</h2>
                <div className="grid gap-3">
                  {[
                    "You must provide accurate information in your base CV.",
                    "You may not upload content that is illegal, obscene, defamatory, or violates any third party's rights.",
                    "You may not upload content containing malware, viruses, or any harmful code.",
                    "You may not use the service to generate fraudulent, misleading, or deceptive resumes.",
                    "You may not attempt to circumvent usage limits or access controls.",
                    "You may not use automated tools to access the service beyond normal use."
                  ].map((rule, idx) => (
                    <div key={idx} className="flex gap-3 text-white/50 text-lg">
                      <span className="text-white/20">•</span>
                      <p>{rule}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Section 6-7 */}
          <div className="grid md:grid-cols-2 gap-12 border-t border-white/5 pt-12">
            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Age requirement</h2>
              <p className="text-white/50 leading-relaxed text-lg">You must be at least 18 years old to use Alvaru. By using the service, you represent and warrant that you meet this age requirement.</p>
            </section>
            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Plans and billing</h2>
              <p className="text-white/50 leading-relaxed text-lg">Weekly plans: Free (1 CV), Starter ($9, 50 CVs), and Pro ($19, 200 CVs). Prices and limits are subject to change. Unused generations do not roll over. Alvaru offers three plans.</p>
            </section>
          </div>

          {/* Section 8-9 */}
          <section className="group border-t border-white/5 pt-12 text-lg">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <ShieldCheck className="w-5 h-5 text-red-400" />
              </div>
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-semibold mb-3">8. Limitation of liability</h2>
                  <p className="text-white/50 leading-relaxed italic">
                    Alvaru is provided "as is" and "as available" without warranties of any kind. We are not liable for direct, indirect, or consequential damages including missed job opportunities or inaccurate content.
                  </p>
                </div>
                <div>
                  <h2 className="text-2xl font-semibold mb-3">9. Indemnification</h2>
                  <p className="text-white/50 leading-relaxed">
                    You agree to hold harmless Alvaru and its owners from any claims arising from your use of the service or the content you generate.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 10-13 */}
          <div className="grid md:grid-cols-2 gap-8 text-lg border-t border-white/5 pt-12">
            <div>
              <h2 className="text-xl font-bold mb-2">10. Third-party services</h2>
              <p className="text-sm text-white/40">Powered by Anthropic. We are not responsible for third-party failures or AI behavior changes.</p>
            </div>
            <div>
              <h2 className="text-xl font-bold mb-2">11. Refunds</h2>
              <p className="text-sm text-white/40">Subscriptions are non-refundable once content generation has begun. EU residents have 14-day withdrawal rights if unused.</p>
            </div>
            <div>
              <h2 className="text-xl font-bold mb-2">12. Termination</h2>
              <p className="text-sm text-white/40">We may suspend or terminate accounts for violations. You may stop using the service at any time.</p>
            </div>
            <div>
              <h2 className="text-xl font-bold mb-2">13. Changes to terms</h2>
              <p className="text-sm text-white/40">Alvaru reserves the right to update these terms. Continued use after updates constitutes acceptance of the new terms.</p>
            </div>
          </div>

          {/* Section 14-15 */}
          <section className="group border-t border-white/5 pt-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="mt-1 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 transition-colors">
                <Gavel className="w-5 h-5 text-blue-400" />
              </div>
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-semibold mb-3">14. Dispute resolution</h2>
                  <p className="text-white/50 leading-relaxed text-lg">
                    Disputes shall be attempted informally first. If unresolved in 30 days, legal resolution may proceed in specified courts. No class actions or representative proceedings allowed.
                  </p>
                </div>
                <div>
                  <h2 className="text-2xl font-semibold mb-3 italic">15. Governing law</h2>
                  <p className="text-white/50 leading-relaxed text-lg">
                    Governed by the laws of the <strong className="text-white/80 underline decoration-white/20">United Kingdom</strong>. Exclusive jurisdiction in UK courts. By using the service from outside the UK, you consent to the transfer and processing of your data in the UK.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 16 */}
          <section className="group border-t border-white/5 pt-12 mb-12">
            <div className="p-8 rounded-3xl bg-blue-600/10 border border-blue-500/20 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl font-bold mb-2 uppercase italic text-white leading-none">Legal Help?</h2>
                <p className="text-white/50">For questions about these terms or legal inquiries.</p>
              </div>
              <a href="mailto:info@alvaru.com">
                <Button size="lg" className="bg-white text-black hover:bg-white/90 gap-2 h-14 px-8 rounded-2xl font-bold">
                  <Mail className="w-5 h-5" />
                  info@alvaru.com
                </Button>
              </a>
            </div>
          </section>
        </motion.div>

        {/* Footer info */}
        <div className="text-center text-white/20 text-sm border-t border-white/5 pt-12 uppercase tracking-widest">
          &copy; 2026 Alvaru. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
